import { Routes, Route, Navigate } from 'react-router-dom';

import { RequirePlatformAdmin } from './guards/RequirePlatformAdmin';
import { RequireUnauthed } from './guards/RequireUnauthed';
import { AdminShell } from './shell/AppShell/index';

import AuthLayout from '../pages/auth/index';
import Login from '../pages/auth/login/index';
import ForgotPassword from '../pages/auth/forgot-password/index';
import ResetPassword from '../pages/auth/reset-password/index';
import Mfa from '../pages/auth/mfa/index';
import MfaSetup from '../pages/auth/mfa-setup/index';

import Dashboard from '../pages/dashboard/index';

import Tenants from '../pages/tenants/index';
import TenantView from '../pages/tenants/view[id]/index';

import Users from '../pages/users/index';
import UserView from '../pages/users/view[id]/index';

import Roles from '../pages/roles/index';
import Broadcasts from '../pages/broadcasts/index';
import Conversations from '../pages/conversations/index';
import ApiMonitoring from '../pages/api-monitoring/index';
import QueueMonitoring from '../pages/queue-monitoring/index';

import Billing from '../pages/billing/index';
import SkuRates from '../pages/billing/sku-rates/index';
import Ledger from '../pages/billing/ledger/index';
import Subscriptions from '../pages/billing/subscriptions/index';
import Plans from '../pages/billing/plans/index';
import Itn from '../pages/billing/itn/index';
import Invoices from '../pages/billing/invoices/index';
import BillingUsage from '../pages/billing/usage/index';
import WalletAlerts from '../pages/billing/wallet-alerts/index';
// Meta news feed (15 September 2026)
import MetaUpdates from '../pages/billing/meta-updates/index';
// AI models (14 October 2026)
import AiModels from '../pages/billing/ai-models/index';
// Languages chatbots speak (1 November 2026)
import Languages from '../pages/billing/languages/index';
import LanguageLines from '../pages/billing/languages/view[code]/index';

import Templates from '../pages/templates/index';
import TemplateView from '../pages/templates/view[id]/index';

import Gateways from '../pages/gateways/index';
import GatewayNumbers from '../pages/gateways/numbers/index';
import GatewayWba from '../pages/gateways/wba/index';
import MetaStatusPage from '../pages/gateways/meta-status/index';

import Media from '../pages/media/index';
import MediaKb from '../pages/media/kb/index';

import Webhooks from '../pages/webhooks/index';
import WebhookDeliveries from '../pages/webhooks/deliveries/index';

import Flows from '../pages/flows/index';
import FlowView from '../pages/flows/view[id]/index';
import AssistantTranscripts from '../pages/flows/assistant/index';

import Audit from '../pages/audit/index';

import Logs from '../pages/logs/index';
import LogsQuery from '../pages/logs/query/index';

import System from '../pages/system/index';
import SystemHealth from '../pages/system/health/index';
import SystemAlerts from '../pages/system/alerts/index';
import SystemBackups from '../pages/system/backups/index';
import SystemSecurity from '../pages/system/security/index';

import SettingsLayout from '../pages/settings/_layout/index';
import Settings from '../pages/settings/index';
import SettingsProfile from '../pages/settings/profile/index';
import SettingsAppearance from '../pages/settings/appearance/index';
import SettingsSecurity from '../pages/settings/security/index';
import SettingsApiKeys from '../pages/settings/api-keys/index';
import SettingsIntegrations from '../pages/settings/integrations/index';
import SettingsFeatureFlags from '../pages/settings/feature-flags/index';
import VoicePage from '../pages/voice/index';
import UssdPage from '../pages/ussd/index';
import ContentNews from '../pages/content/blog/index';
import ContentCompany from '../pages/content/company/index';
import ContentAgreements from '../pages/content/agreements/index';
// W47: the partner programme. Its own nav section rather than a page under
// Billing, because approving people and paying them are different jobs from
// running a price book.
import Partners from '../pages/partners/affiliates/index';
import PartnerWithdrawals from '../pages/partners/withdrawals/index';
import PartnerSettings from '../pages/partners/settings/index';
import SettingsBackup from '../pages/settings/backup/index';

import NotFound from '../pages/not-found/index';

export function AppRoutes() {
  return (
    <Routes>
      {/* Auth group: accessible without a session. */}
      <Route
        path="/auth"
        element={
          <RequireUnauthed>
            <AuthLayout />
          </RequireUnauthed>
        }
      >
        <Route index element={<Navigate to="login" replace />} />
        <Route path="login" element={<Login />} />
        <Route path="forgot-password" element={<ForgotPassword />} />
        <Route path="reset-password" element={<ResetPassword />} />
        <Route path="reset-password/:token" element={<ResetPassword />} />
        <Route path="mfa" element={<Mfa />} />
        <Route path="mfa-setup" element={<MfaSetup />} />
        {/* A mistyped auth URL used to render the layout with an empty card:
            brand, badge, subtitle and nothing to do. Send it to sign-in. */}
        <Route path="*" element={<Navigate to="/auth/login" replace />} />
      </Route>

      {/* Authed app shell. */}
      <Route
        path="/"
        element={
          <RequirePlatformAdmin>
            <AdminShell />
          </RequirePlatformAdmin>
        }
      >
        <Route index element={<Dashboard />} />

        <Route path="tenants" element={<Tenants />} />
        <Route path="tenants/:id" element={<TenantView />} />

        <Route path="users" element={<Users />} />
        <Route path="users/:id" element={<UserView />} />

        <Route path="roles" element={<Roles />} />
        <Route path="broadcasts" element={<Broadcasts />} />
        <Route path="conversations" element={<Conversations />} />
        <Route path="api-monitoring" element={<ApiMonitoring />} />
        <Route path="queue-monitoring" element={<QueueMonitoring />} />

        <Route path="billing" element={<Billing />} />
        <Route path="billing/sku-rates" element={<SkuRates />} />
        {/* Meta news feed (15 September 2026) */}
        <Route path="billing/meta-updates" element={<MetaUpdates />} />
        <Route path="billing/ledger" element={<Ledger />} />
        <Route path="billing/subscriptions" element={<Subscriptions />} />
        <Route path="billing/plans" element={<Plans />} />
        <Route path="billing/itn" element={<Itn />} />
        <Route path="billing/invoices" element={<Invoices />} />
        <Route path="billing/usage" element={<BillingUsage />} />
        <Route path="billing/wallet-alerts" element={<WalletAlerts />} />
        {/* AI models (14 October 2026) */}
        <Route path="billing/ai-models" element={<AiModels />} />
        {/* Languages chatbots speak, and one language's built-in lines (1 November 2026) */}
        <Route path="billing/languages" element={<Languages />} />
        <Route path="billing/languages/:code" element={<LanguageLines />} />

        <Route path="templates" element={<Templates />} />
        <Route path="templates/:id" element={<TemplateView />} />

        <Route path="voice" element={<VoicePage />} />
        <Route path="ussd" element={<UssdPage />} />

        <Route path="gateways" element={<Gateways />} />
        <Route path="gateways/numbers" element={<GatewayNumbers />} />
        <Route path="gateways/wba" element={<GatewayWba />} />
        <Route path="gateways/meta-status" element={<MetaStatusPage />} />

        <Route path="media" element={<Media />} />
        <Route path="media/kb" element={<MediaKb />} />

        <Route path="webhooks" element={<Webhooks />} />
        <Route path="webhooks/deliveries" element={<WebhookDeliveries />} />

        <Route path="flows" element={<Flows />} />
        <Route path="flows/assistant" element={<AssistantTranscripts />} />
        <Route path="flows/:id" element={<FlowView />} />

        <Route path="audit" element={<Audit />} />

        <Route path="logs" element={<Logs />} />
        <Route path="logs/query" element={<LogsQuery />} />

        <Route path="system" element={<System />} />
        <Route path="system/health" element={<SystemHealth />} />
        <Route path="system/alerts" element={<SystemAlerts />} />
        <Route path="system/backups" element={<SystemBackups />} />
        <Route path="system/security" element={<SystemSecurity />} />

        <Route path="content/news" element={<ContentNews />} />
        <Route path="content/company" element={<ContentCompany />} />
        <Route path="content/agreements" element={<ContentAgreements />} />

        <Route path="partners" element={<Partners />} />
        <Route path="partners/withdrawals" element={<PartnerWithdrawals />} />
        <Route path="partners/settings" element={<PartnerSettings />} />

        <Route path="settings" element={<SettingsLayout />}>
          <Route index element={<Settings />} />
          <Route path="profile" element={<SettingsProfile />} />
          <Route path="appearance" element={<SettingsAppearance />} />
          <Route path="security" element={<SettingsSecurity />} />
          <Route path="api-keys" element={<SettingsApiKeys />} />
          <Route path="integrations" element={<SettingsIntegrations />} />
          <Route path="feature-flags" element={<SettingsFeatureFlags />} />
          <Route path="backup" element={<SettingsBackup />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
